import { Injectable, computed, inject, signal } from '@angular/core';
import type { Session } from '@supabase/supabase-js';
import { SupabaseService } from '../supabase/supabase.service';
import type { Database } from '../db/database.types';

export type AuthState = 'checking' | 'signed_out' | 'authenticated';

export type Profile = Pick<
  Database['public']['Tables']['users']['Row'],
  'id' | 'email' | 'full_name' | 'avatar_url' | 'active_account_id' | 'is_superadmin'
>;

/**
 * `offline_access` es lo que hace que Supabase entregue `provider_refresh_token`.
 * Sin él no hay Teams después de recargar la página.
 */
const MICROSOFT_SCOPES = [
  'email',
  'profile',
  'offline_access',
  'User.Read',
  'User.ReadBasic.All',
  'Chat.ReadWrite',
  'Chat.Create',
  'ChannelMessage.Send',
].join(' ');

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly supabase = inject(SupabaseService);

  readonly session = signal<Session | null>(null);
  readonly profile = signal<Profile | null>(null);
  readonly state = signal<AuthState>('checking');

  readonly isAuthenticated = computed(() => this.state() === 'authenticated');
  readonly isLoading = computed(() => this.state() === 'checking');
  readonly isSuperAdmin = computed(() => this.profile()?.is_superadmin === true);

  constructor() {
    if (!this.supabase.isConfigured) {
      this.state.set('signed_out');
      return;
    }

    this.supabase.auth.onAuthStateChange((_event, session) => {
      void this.applySession(session);
    });
    void this.bootstrap();
  }

  async signInWithMicrosoft(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: MICROSOFT_SCOPES,
        redirectTo: this.callbackUrl(),
        queryParams: { prompt: 'select_account' },
      },
    });

    if (error) throw error;
  }

  async signInWithGoogle(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: this.callbackUrl() },
    });

    if (error) throw error;
  }

  /**
   * Enlace mágico. No hay contraseña en ningún camino de entrada, así que el
   * correo es la alternativa para quien no tiene cuenta de trabajo en Microsoft
   * ni en Google — no un segundo factor ni un plan B de los proveedores.
   *
   * `shouldCreateUser` se deja en su valor por defecto: el trigger
   * `handle_new_auth_user` levanta usuario, cuenta personal y membresía igual
   * que con OAuth, así que entrar por correo la primera vez es un alta válida.
   */
  async signInWithEmailLink(email: string): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: this.callbackUrl() },
    });

    // El SDK devuelve el fallo en el resultado, no lo lanza: sin esto el login
    // anunciaría "te enviamos un enlace" aunque el envío no haya salido.
    if (error) throw error;
  }

  async signOut(): Promise<void> {
    await this.supabase.auth.signOut();
    this.session.set(null);
    this.profile.set(null);
    this.state.set('signed_out');
  }

  private async bootstrap(): Promise<void> {
    const { data } = await this.supabase.auth.getSession();
    await this.applySession(data.session);
  }

  private async applySession(session: Session | null): Promise<void> {
    this.session.set(session);

    if (!session) {
      this.profile.set(null);
      this.state.set('signed_out');
      return;
    }

    await this.loadProfile(session.user.id);
  }

  private async loadProfile(userId: string): Promise<void> {
    const { data, error } = await this.supabase.client
      .from('users')
      .select('id, email, full_name, avatar_url, active_account_id, is_superadmin')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      this.state.set('signed_out');
      return;
    }

    this.profile.set(data);
    this.state.set('authenticated');

    // Sin await: un fallo al registrar el último acceso no debe bloquear la entrada.
    void this.supabase.client
      .from('users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', userId);
  }

  private callbackUrl(): string {
    if (typeof window === 'undefined') return '/auth/callback';
    return `${window.location.origin}/auth/callback`;
  }
}
